import React, { useMemo, useRef } from "react";
import { safeDate } from "../../../../utils/dateUtils";
import { exportElementToPdf } from "../../../../utils/pdfExport";
import Button from "../../../common/Button";
import "./ClinicalTimeline.css";

export interface ChartHistoryRecord {
  id?: number;
  visit_id?: number;
  tooth?: string | null;
  symbol?: string;
  step_key?: string | null;
  step_label?: string | null;
  surface?: string | null;
  multi_tooth?: string | null;
  procedure?: string | null;
  timestamp?: string;
  [key: string]: unknown;
}

export interface TimelineEventData {
  id?: string;
  clinical_id?: string;
  created_at?: string | number;
  timestamp?: string | number;
  time?: string | number;
  title?: string;
  description?: string;
  treatment_status?: string;
  status?: string;
  procedure?: string | { name?: string };
  procedure_name?: string;
  procedure_type?: string;
  tooth?: string;
  tooth_number?: string;
  tooth_no?: string;
  target_ref?: string;
  symbol?: string;
  step_key?: string;
  step_label?: string;
  surface?: string | null;
  multi_tooth?: string | null;
  doctor_notes?: string;
  notes?: string;
  tooth_visual?: string;
  visual_code?: string;
  procedure_scope?: string;
  [key: string]: unknown;
}

export function mapChartHistoryToTimelineEvent(
  raw: ChartHistoryRecord,
  index: number
): TimelineEventData {
  const id = raw.id != null ? String(raw.id) : `ch-${index}`;
  const title =
    (raw.procedure && String(raw.procedure).trim()) ||
    (raw.step_label && String(raw.step_label).trim()) ||
    (raw.symbol && String(raw.symbol).trim()) ||
    "Chart activity";
  const parts: string[] = [];
  if (raw.step_label) parts.push(String(raw.step_label));
  if (raw.tooth) parts.push(`Tooth ${raw.tooth}`);
  if (raw.surface) parts.push(`Surface ${raw.surface}`);
  const description =
    parts.length > 0 ? parts.join(" · ") : (raw.symbol ? `Chart: ${raw.symbol}` : "");
  return {
    id,
    timestamp: raw.timestamp ?? undefined,
    title,
    description,
    status: "completed",
    procedure: raw.procedure ?? undefined,
    procedure_name: title,
    tooth: raw.tooth ?? undefined,
    step_key: raw.step_key ?? undefined,
    step_label: raw.step_label ?? undefined,
    symbol: raw.symbol ?? undefined,
    surface: raw.surface ?? undefined,
    multi_tooth: raw.multi_tooth ?? undefined,
    notes: description || undefined,
  };
}

function normalizeEvents(
  events: Array<TimelineEventData | ChartHistoryRecord>
): TimelineEventData[] {
  return events.map((e, i) => {
    if (isChartHistoryRecord(e)) {
      return mapChartHistoryToTimelineEvent(e, i);
    }
    const ev = e as TimelineEventData;
    return {
      ...ev,
      id: ev.id ?? (ev.clinical_id ?? String(i)),
      status: ev.status ?? ev.treatment_status ?? "planned",
      title: ev.title ?? ev.procedure_name ?? (typeof ev.procedure === "string" ? ev.procedure : null) ?? ev.symbol ?? "Unspecified",
      description: ev.description ?? ev.notes ?? ev.doctor_notes ?? "",
    };
  });
}

function isChartHistoryRecord(
  e: TimelineEventData | ChartHistoryRecord
): e is ChartHistoryRecord {
  const r = e as Record<string, unknown>;
  return (
    r != null &&
    typeof r === "object" &&
    ("symbol" in r || "procedure" in r) &&
    !("title" in r && r.title !== undefined)
  );
}

const getStatusConfig = (status: string | undefined): { class: string; icon: string; label: string } => {
  const s = (status || "").toLowerCase();

  if (s.includes("complete"))
    return { class: "completed", icon: "✓", label: "Done" };

  if (s.includes("progress"))
    return { class: "inprogress", icon: "⟳", label: "In Progress" };

  return { class: "planned", icon: "○", label: "Planned" };
};

const getProcedureIcon = (name: string | undefined): string => {
  const n = (name || "").toLowerCase();
  if (n.includes("extract") || n.includes("missing")) return "🦷";
  if (n.includes("crown") || n.includes("veneer")) return "👑";
  if (n.includes("root") || n.includes("rct")) return "⚡";
  if (n.includes("fill") || n.includes("restor")) return "✨";
  if (n.includes("implant")) return "🔩";
  if (n.includes("scale") || n.includes("clean")) return "💧";
  return "📋";
};

function getEventTime(event: TimelineEventData | null | undefined): Date | null {
  const raw = event?.created_at || event?.timestamp || event?.time || null;
  return safeDate(raw);
}

interface TimelineEventProps {
  event: TimelineEventData;
  isLast: boolean;
  onClick?: (event: TimelineEventData) => void;
}

function TimelineEvent({ event, isLast, onClick }: TimelineEventProps) {
  const dt = useMemo(() => getEventTime(event), [event]);
  const timeLabel = dt ? dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "--";

  if (!event) return null;

  const treatmentStatus = event.status ?? event.treatment_status ?? "planned";
  const { class: statusClass, icon: statusIcon, label: statusLabel } = getStatusConfig(treatmentStatus);
  const title = event.title ?? (typeof event.procedure === "object" && event.procedure?.name ? event.procedure.name : (event.procedure_name || event.procedure_type || (typeof event.procedure === "string" ? event.procedure : null) || event.symbol || "Unspecified Procedure"));
  const description = event.description ?? event.notes ?? event.doctor_notes ?? "";
  const procIcon = getProcedureIcon(title);
  const toothNo = event.tooth_number || event.tooth_no || event.tooth || event.target_ref || null;
  const notes = description || event.doctor_notes || event.notes || "";
  const visualCode = event.tooth_visual || event.visual_code || null;

  return (
    <div
      className={`timeline-event ${statusClass} ${isLast ? "is-last" : ""} ${onClick ? "is-clickable" : ""}`}
      onClick={() => onClick?.(event)}
      role="button"
      tabIndex={0}
    >
      <div className="track-col">
        <div className="timeline-marker">
          {statusIcon}
        </div>
        {!isLast && <div className="timeline-line"></div>}
      </div>

      <div className="content-col">
        <div className="event-card">
        <div className="event-header">
          <div className="title-row">
            <span className="proc-icon">{procIcon}</span>
            <strong className="proc-name">{title}</strong>
          </div>
          
          <div className="event-tags">
            {toothNo && toothNo !== "UPPER" && toothNo !== "LOWER" && toothNo !== "FULL" && (
              <span className="tooth-badge">
                #{toothNo}
              </span>
            )}
            {event.procedure_scope === "arch" && (
              <span className="arch-badge">
                {toothNo === "UPPER" ? "Upper Arch" : toothNo === "LOWER" ? "Lower Arch" : "Full Mouth"}
              </span>
            )}
            {event.procedure_scope === "visit" && (
              <span className="visit-badge">
                General
              </span>
            )}
            {event.step_key && (
              <span className="step-badge">
                {event.step_label || event.step_key}
              </span>
            )}
          </div>
        </div>

        <div className="event-meta">
          <span className="status-pill">
            {statusLabel}
          </span>
          {visualCode && (
            <span className="visual-pill">👁 {visualCode.replace(/_/g, " ")}</span>
          )}
          <span className="time-stamp">{timeLabel}</span>
        </div>

        {event.step_key && (
          <div className="event-step-info">
            <span className="step-indicator">Step:</span> {event.step_label || event.step_key}
          </div>
        )}

        {notes && (
          <div className="event-notes">
            <p>{notes}</p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

export type ClinicalTimelineEventInput = TimelineEventData | ChartHistoryRecord;

export interface ClinicalTimelineProps {
  events?: ClinicalTimelineEventInput[];
  onResume?: (event: TimelineEventData) => void;
}

export default function ClinicalTimeline({ events = [], onResume }: ClinicalTimelineProps) {
  const normalized = useMemo(
    () => normalizeEvents(events as Array<TimelineEventData | ChartHistoryRecord>),
    [events]
  );
  const exportRef = useRef<HTMLDivElement | null>(null);

  const grouped = useMemo(() => {
    if (!normalized.length) return {} as Record<string, TimelineEventData[]>;

    const sorted = [...normalized].sort((a, b) => {
      const da = getEventTime(a)?.getTime() ?? 0;
      const db = getEventTime(b)?.getTime() ?? 0;
      return db - da;
    });

    return sorted.reduce<Record<string, TimelineEventData[]>>((acc, ev) => {
      const dt = getEventTime(ev);
      if (!dt) return acc;
      const dateStr = dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
      if (!acc[dateStr]) acc[dateStr] = [];
      acc[dateStr].push(ev);
      return acc;
    }, {});
  }, [normalized]);

  if (!normalized.length) {
    return (
      <div className="timeline-empty">
        <div className="empty-icon">📂</div>
        <h4>No history yet.</h4>
        <p>Procedures added will appear here.</p>
      </div>
    );
  }

  const handleExportPdf = async () => {
    await exportElementToPdf(exportRef.current as HTMLElement, { filename: "clinical-timeline.pdf" });
  };

  return (
    <div className="timeline-container">
      <div className="timeline-header">
        <h4 className="timeline-title">
          Patient History
          <span className="badge-count">
            {normalized.length}
          </span>
        </h4>
        <Button variant="outline" size="sm" onClick={handleExportPdf}>
          Export PDF
        </Button>
      </div>

      <div className="timeline-body" ref={exportRef}>
        {Object.keys(grouped).map((date) => (
          <div key={date} className="date-group">
            <div className="date-label sticky">
              {date}
            </div>
            <div className="events-list">
              {grouped[date].map((ev, i) => (
                <TimelineEvent
                  key={ev.clinical_id || ev.id || i}
                  event={ev}
                  isLast={i === grouped[date].length - 1}
                  onClick={onResume}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
